import { useMemo, type ReactNode, type CSSProperties } from 'react';
import {
  Flag, CheckCircle2, ArrowRight, Hash, User as UserIcon,
  Image as ImageIcon, FileText, Bell, Loader2,
} from 'lucide-react';
import type { PendingMessageData } from '@/services/firebase/chat';

export interface PendingMessagesPanelProps {
  items: PendingMessageData[];
  /** Resolve o rótulo (nome + se é DM) de cada conversa para o cabeçalho da seção. */
  channelLabel: (channelId: string) => { name: string; isDm: boolean };
  currentUserId: string;
  onGoTo: (channelId: string, messageId: string) => void;
  onResolve: (channelId: string, messageId: string) => void;
  /** Layout mais enxuto para o chat flutuante. */
  compact?: boolean;
  /** `${channelId}/${messageId}` da pendência em processo de resolução. */
  resolvingId?: string | null;
}

function formatWhen(ts: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function previewOf(item: PendingMessageData): { icon: ReactNode; text: string } {
  switch (item.type) {
    case 'image':
      return { icon: <ImageIcon size={12} className="shrink-0 opacity-70 mt-0.5" />, text: item.text || 'Imagem' };
    case 'file':
      return { icon: <FileText size={12} className="shrink-0 opacity-70 mt-0.5" />, text: item.text || 'Arquivo' };
    case 'attention':
      return { icon: <Bell size={12} className="shrink-0 opacity-70 mt-0.5" />, text: 'Pedido de atenção' };
    default:
      return { icon: null, text: item.text };
  }
}

const clamp2: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/**
 * Lista de mensagens marcadas como "pendentes de resposta", agrupada por
 * conversa (uma seção por canal/DM). Compartilhada entre o chat flutuante e a
 * página de chat. A lógica de marcar/resolver/assinar fica no ChatService; aqui
 * só apresentamos e disparamos os callbacks.
 */
export const PendingMessagesPanel = ({
  items,
  channelLabel,
  currentUserId,
  onGoTo,
  onResolve,
  compact = false,
  resolvingId = null,
}: PendingMessagesPanelProps) => {
  const groups = useMemo(() => {
    const map = new Map<string, PendingMessageData[]>();
    for (const it of items) {
      const arr = map.get(it.channelId);
      if (arr) arr.push(it);
      else map.set(it.channelId, [it]);
    }
    // Dentro de cada conversa: mais recém-marcada primeiro.
    for (const arr of map.values()) arr.sort((a, b) => b.markedAt - a.markedAt);
    // Conversas: a que tem a pendência mais recente aparece no topo.
    return Array.from(map.entries()).sort((a, b) => {
      const am = a[1].reduce((mx, x) => Math.max(mx, x.markedAt), 0);
      const bm = b[1].reduce((mx, x) => Math.max(mx, x.markedAt), 0);
      return bm - am;
    });
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6 py-10 text-on-surface-variant">
        <Flag size={compact ? 22 : 28} className="text-amber-400/60" />
        <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium`}>Nenhuma mensagem pendente</p>
        <p className="text-[11px] text-on-surface-variant/70 max-w-[240px]">
          Passe o mouse sobre uma mensagem e clique na bandeirinha para deixá-la
          pendente de resposta. Ela fica aqui até alguém resolver.
        </p>
      </div>
    );
  }

  const pad = compact ? 'px-2.5' : 'px-4';

  return (
    <div className="flex flex-col">
      {groups.map(([channelId, groupItems]) => {
        const label = channelLabel(channelId);
        return (
          <div key={channelId} className="border-b border-outline-variant/10 last:border-b-0">
            <div className={`${pad} py-1.5 sticky top-0 bg-surface-container/95 backdrop-blur-sm flex items-center gap-1.5 text-[11px] font-bold text-on-surface-variant z-10`}>
              {label.isDm ? <UserIcon size={12} className="shrink-0" /> : <Hash size={12} className="shrink-0" />}
              <span className="truncate">{label.name}</span>
              <span className="ml-auto text-[10px] font-semibold bg-amber-400/20 text-amber-600 rounded-full px-1.5 py-0.5">
                {groupItems.length}
              </span>
            </div>

            {groupItems.map((it) => {
              const preview = previewOf(it);
              const key = `${it.channelId}/${it.messageId}`;
              const resolving = resolvingId === key;
              return (
                <div
                  key={key}
                  className={`${pad} py-2 flex items-start gap-2 hover:bg-surface-container-high/50 transition-colors`}
                >
                  <Flag size={12} className="text-amber-500 fill-amber-500/30 shrink-0 mt-0.5" />
                  <button
                    type="button"
                    onClick={() => onGoTo(it.channelId, it.messageId)}
                    className="min-w-0 flex-1 text-left group/item"
                    title="Ir para a conversa"
                  >
                    <div className="flex items-start gap-1 text-xs text-on-surface group-hover/item:text-primary transition-colors">
                      {preview.icon}
                      <span style={clamp2} className="break-words">{preview.text || '(sem texto)'}</span>
                    </div>
                    <div className="text-[10px] text-on-surface-variant/70 mt-0.5 truncate">
                      <span className="font-semibold">{it.senderName || 'Alguém'}</span>
                      {it.messageTimestamp ? ` · ${formatWhen(it.messageTimestamp)}` : ''}
                      {it.markedByName ? ` · marcou: ${it.markedBy === currentUserId ? 'você' : it.markedByName}` : ''}
                    </div>
                  </button>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => onGoTo(it.channelId, it.messageId)}
                      className="p-1 rounded text-on-surface-variant/70 hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Ir para a conversa"
                      aria-label="Ir para a conversa"
                    >
                      <ArrowRight size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onResolve(it.channelId, it.messageId)}
                      disabled={resolving}
                      className="p-1 rounded text-on-surface-variant/70 hover:text-green-600 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                      title="Marcar como resolvida"
                      aria-label="Marcar como resolvida"
                    >
                      {resolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
